import get from 'lodash/get';
import { isEmpty, isNil, keyBy, map, omitBy, sortBy, toString } from 'lodash/fp';

import getPlayersFromGames from '../utils/getPlayersFromGames';
import decodeTeam from './decodeTeam';
import decodeBonus from './decodeBonus';
import decodeChallengeEntry from './decodeChallengeEntry';
import decodeCompetitor from './decodeCompetitor';
import decodeGame from './decodeGame';
import convertCurrency from '../utils/convertCurrency';
import { RACING_CHALLENGE, RAPID_FIRE } from '../constants/challengeTypes';
import { TICKET_CHALLENGE, H2H, TARGET_CHALLENGE } from '../constants/challengeSubTypes';
import {
  getPlayerBoardTableInfo,
  getPlayerSelectionTableInfo
  // isFantasyInsiderSport
} from '../utils/challengeUtils';

const keyById = keyBy('id');
const mapEntries = map(decodeChallengeEntry);
const omitNil = omitBy(isNil);

export function normalizeLabel(label = {}, index) {
  return {
    bgColor: label.bg_color,
    description: label.description,
    id: index,
    name: label.name,
    textColor: label.text_color
  };
}

// NOTE: there are 2 position objects. position rules and player position.
// We don't normalize the player position so it uses key.
// For consistency and safety we'll keep both id and key here until we can
// confidently clean it up. I suggest we should normalize player position to use id
// as key is a problematic with React code.
function normalizePlayerPosition(pos = {}) {
  return {
    abbr: pos.short_label,
    id: pos.key,
    key: pos.key,
    max: parseInt(pos.max, 10),
    min: parseInt(pos.min, 10),
    name: pos.label,
    shareQuota: !!pos.include_teams
  };
}

export function normalizeTeamPosition(selectionRules) {
  const sharedPosition = get(selectionRules, 'position_rules', []).find(r => r.include_teams);
  const rule = get(selectionRules, 'team_selections') || sharedPosition;

  if (!rule) return {};
  const key = rule.short_label ? rule.short_label.toLowerCase() : undefined;

  return omitNil({
    abbr: rule.short_label,
    id: rule.key || key,
    key: rule.key || key,
    max: rule.required_selections || 0,
    min: rule.required_selections || 0,
    name: rule.label,
    isTeam: true,
    shareQuota: !!sharedPosition
  });
}

export function getTeams(games = [], position) {
  return games.reduce((memo, game) => {
    const countryCode = get(game, 'competition.competition_type.country.iso_alpha_2_code');
    const showProgress = !get(game, 'competition.competition_type.sport.game_progress_irrelevant');

    return get(game, 'teams', []).reduce(
      (obj, team) =>
        Object.assign(obj, {
          [team.id]: {
            awayAbbr: game.away_team_abbreviation || game.away_team_name,
            awayTeam: game.away_team_name,
            gameId: toString(game.id),
            homeAbbr: game.home_team_abbreviation || game.home_team_name,
            homeTeam: game.home_team_name,
            onesportCompetitionId: get(game, 'competition.onesport_id'),
            onesportGameId: get(game, 'onesport_id'),
            progress: showProgress ? game.progress : undefined,
            reverseOrder: countryCode === 'US',
            statusId: get(game, 'status.id'),
            ...decodeTeam(team, position, game)
          }
        }),
      memo
    );
  }, {});
}

function decodeCountry(country) {
  return {
    abbr: country.iso_alpha_3_code,
    flag: country.flag,
    id: country.id,
    name: country.name
  };
}

function decodeChallenge(data = {}, meta = {}) {
  const discount = data.coupon ? parseInt(data.coupon.discount, 10) : 0;
  const ruleSet = data.rule_set || {};
  const userEntry = decodeCompetitor(data.current_user_entry);
  const selectionRules = ruleSet.selection_rules;
  const teamPosition = normalizeTeamPosition(selectionRules);
  const bonuses = get(selectionRules, 'bonus_selections', []).reduce((memo, item) => {
    const bonus = decodeBonus(item);
    return Object.assign({}, memo, {
      [bonus.key]: bonus
    });
  }, {});
  const maxEntriesPerPerson = data.cap_entries_per_person;
  const userEntryIds = data.current_user_entry_ids || [];
  const isTrainingChallenge = data.trainer;
  const isRapidfire = data.challenge_type_id === RAPID_FIRE;
  const cost = convertCurrency(data.total_fee, meta);

  let linkPath;
  if (isTrainingChallenge) {
    linkPath = '/training/challenge';
  } else if (isRapidfire) {
    linkPath = '/rapidfire/challenge';
  } else {
    linkPath = '/challenge';
  }

  const myEntries = mapEntries(data.current_user_entries);

  const statusId = get(data, 'status.id');
  const statusLabel = get(data, 'status.name');
  const startTime = new Date(data.start_time).getTime();

  const shareQuota = get(selectionRules, 'required_selections') > 0;

  const isRacing = data.challenge_type_id === RACING_CHALLENGE || !!data.is_leg;
  const showPlayerSelections = data.display_selected_percentage;
  const competitionIcon = (get(data, 'competition_type.images') || []).find(
    imgSrc => imgSrc.slice(-4) === '.svg'
  );

  // this is actually competitionTypeId
  const competitionId = get(data, 'competition_type.id') || get(data, 'competition_type_id');
  const competitionTypeId = get(data, 'competition_type_id');
  const isH2H = get(data, 'challenge_sub_type.id') === H2H;
  const userLevels = get(data, 'user_levels.levels');
  const rookieOnly = data.rookie_only;

  return omitNil({
    rookieOnly,
    bonuses,
    competitionId, // we should remove this and use competitionTypeId
    competitionTypeId,
    competitionIcon,
    competitionName: get(data, 'competition_type.short_name'),
    competitors: data.entries ? data.entries.map(decodeCompetitor) : null,
    cost,
    countries: data.countries ? data.countries.map(decodeCountry) : [],
    discount,
    enableSmartLineUp: false, // isFantasyInsiderSport(competitionId),
    entrantNames: data.entrant_names,
    entryCap: data.cap_entries,
    entryCount: data.entry_count,
    gameCount: data.game_count,
    games: data.games
      ? sortBy(
          'startTime',
          data.games.map((g, i) => decodeGame({ ...g, gameOrder: i + 1 }))
        )
      : null,
    hasEntered: data.entered,
    hasWon: userEntry.hasWon,
    id: toString(data.id),
    isCaptainContest: data.is_captain_contest,
    isDrop1: data.is_drop1,
    isFeatured: data.featured,
    isH2H,
    isLocked: data.is_locked,
    isPrivate: data.private,
    isRacing,
    isRapidfire,
    isTargetChallenge: get(data, 'challenge_sub_type.id') === TARGET_CHALLENGE,
    isTrainingChallenge,
    isTicketChallenge: get(data, 'challenge_sub_type.id') === TICKET_CHALLENGE,
    isUnlimited: !!data.is_unlimited,
    isVisible: data.visible,
    labels: data.labels ? data.labels.map(normalizeLabel) : [],
    link: `${linkPath}/${data.id}`,
    maxEntriesPerPerson,
    minGamesPerEntry: get(selectionRules, 'minimum_players_from_game', 0),
    playersPerGame: get(selectionRules, 'players_in_game'),
    myEntries,
    name: data.name,
    onesportCompetitionId: toString(get(data, 'competition_type.onesport_id')), // DO NOT USE
    onesportSportId: toString(get(data, 'competition_type.sport.onesport_id')),
    order: data.order,
    ownerName: data.owner_name,
    ownerType: data.owner_type,
    payout: data.pay_to,
    payoutIcon: get(data, 'payout_type.icon', ''),
    payoutLongDescription: get(data, 'payout_type.long_description'),
    payoutMessage: get(data, 'payout_type.description'),
    payoutType: get(data, 'payout_type.key'),
    playerBoardTableInfo: getPlayerBoardTableInfo({ isRacing, showPlayerSelections }),
    playerPositions: keyById(
      get(selectionRules, 'position_rules', []).map(normalizePlayerPosition)
    ),
    playerQuota:
      get(selectionRules, 'required_players') || get(selectionRules, 'required_selections') || 0,
    players: getPlayersFromGames(data.games),
    playersCount: userEntry.playersCount,
    playerSelectionTableInfo: getPlayerSelectionTableInfo({ isRacing, showPlayerSelections }),
    playersPerSideCap:
      get(selectionRules, 'players_in_team.max') ||
      get(selectionRules, 'selections_in_team.max') ||
      0,
    prizePool: data.prize_pool,
    prizePoolValue: convertCurrency(data.prize_pool_value, meta),
    prizes: data.prize_brackets,
    racingType: get(data, 'competition_type.short_name', '').split(' ')[0],
    rank: get(data, 'current_user_score.best_position'),
    rapidfireName: get(data, 'rapidfire_name') || data.name,
    rapidfireRuledOutPlayers: get(data, 'rapidfire_challenge.ruleout_players') || [],
    rapidfireTiers: get(selectionRules, 'tiers', []),
    rapidfireType: get(data, 'rapidfire_type'),
    realCompetitionId: get(data, 'competition_id'),
    reducedCost: cost * (1 - discount / 100),
    salaryCap: get(selectionRules, 'salary_cap', 0),
    score: get(data, 'current_user_score.best_fantasy_score'),
    scoring: data.statistic_categories,
    shareQuota,
    sharingMessages: data.sharing_messages,
    showPlayerSelections,
    slateId: data.slate_id,
    slateName: data.slate_name,
    slateType: isH2H ? 'Cash' : 'GPP',
    sportIcon: get(data, 'sports[0].icon'),
    sportId: get(data, 'sports[0].onesport_id'),
    sportName: get(data, 'sports[0].name'),
    startTime,
    statusId,
    statusLabel,
    sticky: !!data.sticky,
    subSportId: get(data, 'sub_sport.id'),
    subTypeId: get(data, 'challenge_sub_type.id'),
    teamPositions: !isEmpty(teamPosition) ? keyById([teamPosition]) : [],
    teamQuota: get(selectionRules, 'team_selections.required_selections', 0),
    teams: getTeams(data.games, teamPosition),
    userEntries: (data.current_user_entries || []).map(decodeCompetitor), // TODO - this is a dupe. See myEntries above!
    userEntryCount: userEntryIds.length,
    userEntryIds,
    userLevels,
    userLevelIcon: get(data, 'user_levels.icon'),
    winnings:
      convertCurrency(get(data, 'current_user_score.total_prize'), meta) ||
      get(data, 'total_winning')
  });
}

export default decodeChallenge;
